<template>
    <v-container class="py-5 px-5" fluid v-if="this.$store.state.signedIn">
        <v-layout justify-center>
            <v-flex xs12 md10 wrap class="py-2 px-2">
                <v-toolbar color="secondary" dark> 
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Query Country for ASNs
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <info>
                        <template>
                            ASNs for a given country
                        </template>
                        <template slot="body">
                            <p> Gives a list of ASNs completely <b> contained </b> within a given country (from reference data)</p>
                            <br>
                            <p> Gives a list of ASNs <b> partially </b> in the given country, along with a breakdown of the other countries present</p>
                            <p> <b>The ASN country breakdown is calculated as follows:</b>
                                <ol>
                                    <li> All prefixes from internet registries (RIPE, APNIC, AFRINIC, LACNIC, LEVEL3) that have this ASN as the owner are stored. </li>
                                    <li> For each of those prefixes, we then take all prefixes with geographic (country) data associated with them, and perform the following aggregation:
                                        <ol type="a">
                                            <li> If we have a direct match, take this as the (only) country information for prefix. </li>
                                            <li> Else, consider all child prefixes (more specific e.g. a /24 child of a /16 prefix), and aggregate upwards.
                                            <i> e.g. A /24 child prefix of a /23 prefix would account for half (1/2) of the IP space </i> </li>
                                            <li> Any remaining space is accounted for by either a parent prefix in registry data (e.g. a /10 parent of a /16 prefix), 
                                                or if there are no parent prefixes, the country where the ASN is registered. </li>
                                        </ol>
                                    </li>
                                    <li> Once we have a geographical breakdown for each prefix owned by an ASN, we then aggregate these again as follows:
                                        <ol type="a">
                                            <li> The geographical breakdown of a given prefix is multiplied by the <b>CIDR (the number after the '/' on a prefix)</b> as an inverse power of 2. This is best explained by comparing an example (the maths is a little hard to understand). </li>
                                            <li> Let's say we have only a /32 prefix and a /31 prefix (both in IPv4 - we will come on to IPv6 later on). The /31 has twice as much IP space as the /32, so accounts for twice as much of the ASN aggregation. </li>
                                            <li> A more polarising example is if we have only a /16 prefix and a /32 prefix. Now the /16 has 2^16 = 65,536 times as much IP space as the /32, hence the /32 is almost insignificant. It is for this reason we often end up with a lot of 'Additional Countries' listed at the bottom of the geographical breakdown. </li>
                                            <li> Where we have an ASN with both IPv4 and IPv6 prefixes, we multiply the IPv4 CIDRs by 4 (since the max of IPv4 CIDRs is 32 and IPv6 is 128, 128/4 = 32). For example, a /16 IPv4 prefix is equivalent to a /48 IPv6 prefix in the calculation.</li>
                                        </ol>
                                    </li>
                                    <li> <b>This is updated daily</b> </li>
                                </ol>
                            </p>
                        </template>
                    </info>
                </v-toolbar>
                <v-card>
                    <v-card-text>
                        <v-layout fluid wrap align-center justify-center class="text-xs-center">
                            <v-flex xs11 md8 class="text-xs-center">
                                <v-form ref="form" lazy-validation>

                                    <v-tooltip top eager>
                                        <template v-slot:activator="{ on }">
                                            <v-autocomplete 
                                            v-on="on"
                                            label="Country"
                                            v-model="country_select"
                                            :items="countryItems"
                                            :search-input="search"
                                            :loading="searchLoading"
                                            cache-items
                                            hide-no-data
                                            :style="$store.state.textFormatting.main"
                                            :rules="refRules"
                                            @change="matchCode"
                                            > </v-autocomplete>
                                        </template>
                                        <span :style="$store.state.textFormatting.headers">Start typing to filter results</span>
                                    </v-tooltip>

                                        
                                </v-form>
                            </v-flex>
                            
                            <v-flex xs0 md2>
                                <v-card tile flat>
                                    <v-card-text v-if="matchedCode" :style="$store.state.textFormatting.main">
                                        Matched Country Code: <b :style="$store.state.textFormatting.main"> {{ matchedCode }} </b>
                                    </v-card-text>
                                </v-card>
                            </v-flex>

                            <v-flex xs1 md1>
                                <v-btn color="green" dark @click="executeQuery" :style="$store.state.textFormatting.main"> Submit </v-btn>
                                <v-progress-circular v-if="loadingData" indeterminate color="purple"> </v-progress-circular>
                            </v-flex>
                        </v-layout>
                        
                    </v-card-text>
                </v-card> 
            </v-flex>
        </v-layout>
        <v-layout justify-center>
            <v-flex xs12 md10 v-if="showMessage" pa-0 ma-0>
                <v-card color="red" pa-0 ma-0>
                    <v-card-text class="text-xs-center pa-0"> <h3 :style="$store.state.textFormatting.main"> {{ resMessage }} </h3> </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>
        <v-layout justify-center>
            <v-flex xs12 md6 v-if="showBreakdown" class="py-2 px-2">
                <v-toolbar color="secondary" dark>
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Breakdown for AS {{ breakdownASN }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn @click="showBreakdown=!showBreakdown" color="red"> <v-icon> close </v-icon> </v-btn>
                </v-toolbar>
                <v-card>
                    <doughnut :chart-data="doughnutData" :options="doughnutoptions"> </doughnut>
                </v-card>
            </v-flex>
            <v-flex xs12 md6 v-if="showResults&!showBreakdown" class="py-2 px-2">
                <v-toolbar color="secondary" dark>
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Fully contained ASNs
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn v-if="listVSautocomplete" @click="listVSautocomplete=!listVSautocomplete" color="success" :style="$store.state.textFormatting.main"> SWITCH TO LIST </v-btn>
                    <v-btn v-if="!listVSautocomplete" @click="listVSautocomplete=!listVSautocomplete" color="success" :style="$store.state.textFormatting.main"> SWITCH TO SELECTOR </v-btn>
                    <info>
                        <template>
                            ASNs fully contained in {{ country_select }}
                        </template>
                        <template slot="body">
                            <p> Can switch between either a list of all the ASNs fully contained in {{ country_select }}, or a search bar which will autocomplete to find ASNs if there's one you'd like to check specifically</p>
                            <br>
                            <p> <b> NOTE: </b> This will not pick up ASNs that have &lt;0.001% of another country, they will appear in the partial list to the right</p>
                        </template>
                    </info>
                </v-toolbar>
                <v-card v-if="listVSautocomplete">
                    <v-layout justify-center>
                        <v-flex xs10>
                            <v-autocomplete
                            label="Start typing to find ASN"
                            v-model="asn_full_search"
                            :items="fullASNList"
                            :search-input="search2"
                            :loading="searchLoading2"
                            cache-items
                            hide-no-data
                            :style="$store.state.textFormatting.main"></v-autocomplete>
                        </v-flex>
                    </v-layout>
                </v-card>
                <v-card v-if="!listVSautocomplete">
                    <v-layout justify-center>
                        <v-flex xs10>
                            <v-data-table :headers="oneHeader" :items="fullASNs" :key="id"
                                :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination">
                                <template slot="items" slot-scope="props">
                                    <tr>
                                        <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn }} </td>
                                    </tr>
                                </template>
                                <template slot="no-data">
                                    <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                    No fully contained ASNs
                                    </v-alert>
                                </template>
                            </v-data-table>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>
            <v-flex xs12 md6 v-if="showResults" class="py-2 px-2">
                <v-toolbar color="secondary" dark>
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Partially contained ASNs
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn v-if="listVSautocomplete2" @click="listVSautocomplete2=!listVSautocomplete2" color="success" :style="$store.state.textFormatting.main"> SWITCH TO LIST </v-btn>
                    <v-btn v-if="!listVSautocomplete2" @click="listVSautocomplete2=!listVSautocomplete2" color="success" :style="$store.state.textFormatting.main"> SWITCH TO SELECTOR </v-btn>
                    <info>
                        <template>
                            ASNs partially contained in {{ country_select }}
                        </template>
                        <template slot="body">
                            <p> Can switch between either a list of all the ASNs fully contained in {{ country_select }}, or a search bar which will autocomplete to find ASNs if there's one you'd like to check specifically</p>
                            <p> <b> NOTE: </b> Countries with &lt;0.01% will display as 0.00% in their breakdown</p>
                            <br>
                            <p> Click an entry in the table or press the breakdown button (on the selector) for a breakdown to appear for the given ASN</p>
                        </template>
                    </info>
                </v-toolbar>
                <v-card v-if="listVSautocomplete2">
                    <v-layout justify-center>
                        <v-flex xs1></v-flex>
                        <v-flex xs6>
                            <v-autocomplete
                            label="Start typing to find ASN"
                            v-model="asn_part_search"
                            :items="partASNList"
                            :search-input="search3"
                            :loading="searchLoading3"
                            cache-items
                            hide-no-data
                            @change="showBDstr=false"
                            :style="$store.state.textFormatting.main"></v-autocomplete>
                        </v-flex>
                        <v-spacer></v-spacer>
                        <v-flex>
                            <v-btn @click="breakItDown" color="success" :style="$store.state.textFormatting.main"> BREAK IT DOWN </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-card v-if="showBDstr">
                        <v-card-text class="text-xs-center">
                            <h4 :style="$store.state.textFormatting.main"> {{ partBDstr }} </h4>
                        </v-card-text>
                    </v-card>
                </v-card>
                <v-card v-if="!listVSautocomplete2">
                    <v-layout justify-center>
                        <v-flex xs10>
                            <v-data-table :headers="twoHeader" :items="partASNs" :key="id"
                                :rows-per-page-items="rowsPerPageItems2" :pagination.sync="pagination2">
                                <template slot="headerCell" slot-scope="props">
                                    <v-tooltip top eager>
                                    <span slot="activator" :style="$store.state.textFormatting.main">
                                        {{ props.header.text }}
                                    </span>
                                    <span :style="$store.state.textFormatting.main">
                                        {{ props.header.ad_text }}
                                    </span>
                                    </v-tooltip>
                                </template>
                                <template slot="items" slot-scope="props">
                                    <tr @click="clickHandler(props.item.asn, props.item.breakdown_dict)">
                                        <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn }} </td>
                                        <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.bdstr }} </td>
                                        <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.perc_specific }}% </td>
                                    </tr>
                                </template>
                                <template slot="no-data">
                                    <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                    No partially contained ASNs
                                    </v-alert>
                                </template>
                            </v-data-table>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>

        </v-layout>
    </v-container>
</template>

<script>

import { API, graphqlOperation } from 'aws-amplify';
import info from '../../shared/operational/infopopout.vue';
import doughnut from '../../shared/developing/doughnut-graph.js'
import { findParentComponentByName } from '../utils/findParent.js'


export default {
    name: 'country_asn',
    components: {
       info,
       doughnut
    },
    computed: {
        cssVars() {
            if (this.$store.state.textFormatting.main ) {
                return {
                    '--textsize': this.$store.state.textFormatting.main + 'em'
                }
            } else {
                return {
                    '--textsize': '18em'
                }
            }
            
        }
    },
    beforeMount () {
        let app = findParentComponentByName(this, 'app')
        app.checkSignedIn()
        if (this.$store.state.searches.countryASNs) {
            this.country_select = this.$store.state.searches.countryASNs
            this.matchCode()
        }
    },
    methods: {
        clickHandler: function (asn, dict) {
            this.doughnutData = false;
            var doughnutDataCountry = this.createdoughnutDataCountry(dict);
            this.breakdownASN = asn;
            this.doughnutData = doughnutDataCountry;
            this.showBreakdown = true;


        },
        breakItDown: async function () {
            var p_asn = this.asn_part_search;
            for(var i = 0; i < this.partASNs.length ; i++) {
                if(this.partASNs[i].asn == p_asn) {
                    this.doughnutData = false;
                    this.partBDstr = this.partASNs[i].bdstr;
                    this.showBDstr = true;
                    var doughnutDataCountry = this.createdoughnutDataCountry(this.partASNs[i].breakdown_dict);
                    this.breakdownASN = p_asn;
                    this.doughnutData = doughnutDataCountry;
                    this.showBreakdown = true;
                }
            }
        },
        matchCode: async function () {
            if (this.country_select.length > 1) {
                this.matchedCode = this.countryCodes[this.country_select];
            } else {
                this.matchedCode = false;
            }

        },
        querySelections (v) {
            this.searchLoading = true
            // Simulated ajax query
            setTimeout(() => {
                this.items = this.countryItems.filter(e => {
                return (e || '').toLowerCase().indexOf((v || '').toLowerCase()) > -1
                })
                this.searchLoading = false
            }, 500)
        },
        asnFullQuerySelections (v) {
            this.searchLoading2 = true
            // Simulated ajax query
            setTimeout(() => {
                this.items = this.fullASNList.filter(e => {
                return (e || '').toLowerCase().indexOf((v || '').toLowerCase()) > -1
                })
                this.searchLoading2 = false
            }, 500)
        },
        asnPartQuerySelections (v) {
            this.searchLoading3 = true
            // Simulated ajax query
            setTimeout(() => {
                this.items = this.partASNList.filter(e => {
                return (e || '').toLowerCase().indexOf((v || '').toLowerCase()) > -1
                })
                this.searchLoading3 = false
            }, 500)
        },
        executeQuery: async function () {
            this.resMessage = false;
            this.showResults = false;
            this.showMessage = false;
            this.showBreakdown = false;
            this.loadingData = true;
            this.fullASNs = [];
            this.partASNs = [];
            this.fullASNList = [];
            this.partASNList = [];

            if(this.$refs.form.validate()) {
                try {
                    this.$store.commit("setSearch", {'countryASNs': this.country_select})
                    var qry = `query getCountryASNs { getCountryASNs (
                        country: "` + this.matchedCode + `") 
                        {
                            asn
                            breakdown_dict
                        }
                    }`
                    console.log(qry);
                    const results = await API.graphql(graphqlOperation(qry));
                    console.log(results);
                    if (!results) {
                        var asns = false;
                    } else {
                        var asns = results.data.getCountryASNs;
                        console.log(asns);
                    }
                    
                } catch(err) {
                    console.log(err)
                    this.resMessage = "Query Error";
                    this.loadingData = false;
                    this.showMessage = true;
                }
                if (!asns) {
                    this.asnResults = [];
                    this.resMessage = "No ASNs found in reference data";
                    this.loadingData = false;
                    this.showMessage = true;
                } else {
                    if (asns.length == 0 || asns === undefined || !asns) {
                        this.asnResults = [];
                    } else {
                        for (var i = 0; i < asns.length ; i++) {
                            //console.log(asns[i].breakdown_dict);
                            try { 
                                var countryBD = JSON.parse(asns[i].breakdown_dict);
                                var countrykeys = Object.keys(countryBD);
                                var bdstr = "";
                                if (countrykeys.length == 1) {
                                    asns[i].bdstr = this.country_select + ": 100%";
                                    asns[i].perc_specific = 100
                                    this.fullASNs.push(asns[i]);
                                    this.fullASNList.push(asns[i].asn);
                                } else {
                                    var country_keys_list = []
                                    for (var j = 0; j < countrykeys.length ; j++) {
                                        country_keys_list.push({
                                            'code': countrykeys[j],
                                            'prop': countryBD[countrykeys[j]]
                                        })
                                        if (countrykeys[j] == this.matchedCode) {
                                            asns[i].perc_specific = Math.round(parseFloat(countryBD[countrykeys[j]]) * 100) / 100
                                        }
                                    }

                                    country_keys_list.sort(function(a, b) {
                                        var keyA = a.prop,
                                            keyB = b.prop;
                                        // Compare the 2 dates
                                        if (keyA < keyB) return 1;
                                        if (keyA > keyB) return -1;
                                        return 0;
                                    });

                                    for (var j = 0; j < country_keys_list.length ; j++) {
                                        if (j == country_keys_list.length - 1) {
                                            var endstr = "%";
                                        } else {
                                            var endstr = "%, ";
                                        }
                                        
                                        try {
                                            if (country_keys_list[j].code == "") {
                                                var country1 = "Unknown"
                                            } else {
                                                var country1 = this.invCountryCodes[country_keys_list[j].code];
                                                if (!country1) {
                                                    country1 = "Unknown (" + country_keys_list[j].code + ")"
                                                }
                                            }
                                        } catch {
                                            var country1 = "Unknown";
                                        }
                                        var numstr = (country_keys_list[j].prop*1).toFixed(2)
                                        if ( numstr == "0.00" ) {
                                            numstr = ""
                                            if ( endstr == "%" ) {
                                                endstr = ""
                                            } else {
                                                endstr = ", "
                                            }
                                        } else {
                                            numstr = ": " + numstr
                                        }
                                        bdstr = bdstr + country1 + numstr + endstr;
                                        
                                    }
                                    asns[i].bdstr = bdstr;
                                    this.partASNs.push(asns[i]);
                                    this.partASNList.push(asns[i].asn);
                                }

                            } catch(err) {
                                console.log(err)
                            }
                            
                            
                        }
                        this.showResults = true;
                        this.loadingData = false;
                        // this.asnResults = asns;

                    }
                }

            }
        },
        createdoughnutDataCountry: function (countryBD) {
            var data = { datasets: [{ data: [], backgroundColor: []}], labels: [] };

            //countryBD = JSON.parse(countryBD.replace(/'/g, '"'))
            countryBD = JSON.parse(countryBD)
            
            
            var countrykeys = Object.keys(countryBD);

            var country_keys_list = []
            for (var j = 0; j < countrykeys.length ; j++) {
                country_keys_list.push({
                    'code': countrykeys[j],
                    'prop': countryBD[countrykeys[j]]
                })
            }

            country_keys_list.sort(function(a, b) {
                var keyA = a.prop,
                    keyB = b.prop;
                // Compare the 2 dates
                if (keyA < keyB) return 1;
                if (keyA > keyB) return -1;
                return 0;
            });
            

            for (var i = 0; i < country_keys_list.length; i=i+1){
                if (country_keys_list[i].prop > 0.005) {
                    if (country_keys_list[i].code == "") {
                        data.labels.push("Unknown")
                    } else {
                        data.labels.push(this.invCountryCodes[country_keys_list[i].code])
                    }
                
                    data.datasets[0].data.push((country_keys_list[i].prop*1).toFixed(2))
                    if (i < this.distinct_colors.length) {
                        var color = this.distinct_colors[i]
                    } else {
                        var color = "hsla(" + ~~(360 * Math.random()) + "," +"50%,"+"50%,1)"
                    }
                    data.datasets[0].backgroundColor.push(color)
                }
            }
            return data


        },
    },
    watch: {
        search (val) {
            val && val != this.country_select && this.querySelections(val)
        },
        search2 (val) {
            val && val != this.asn_full_search && this.asnFullQuerySelections(val)
        },
        search3 (val) {
            val && val != this.asn_partial_search && this.asnPartQuerySelections(val)
        }
    },
    data () {
        return {
            id: null,
            country_select: "",
            breakdownASN: null,
            partBDstr: false,
            showBDstr: false,
            search: null,
            search2: null,
            search3: null,
            searchLoading: false,
            searchLoading2: false,
            searchLoading3: false,
            loadingData: false,
            matchedCode: false,
            items: [],
            rowsPerPageItems: [50, 100],
            pagination: {
                rowsPerPage: 50
            },
            rowsPerPageItems2: [10, 20, 50, 100],
            pagination2: {
                rowsPerPage: 10
            },
            asnResults: [],
            asn_full_search: "",
            asn_part_search: "",
            listVSautocomplete: true,
            listVSautocomplete2: true,
            fullASNs: [],
            fullASNList: [],
            partASNs: [],
            partASNList: [],
            showResults: false,
            showBreakdown: false,
            resMessage: "this is an error message",
            showMessage: false,
            doughnutData: null,
            doughnutoptions: {
                responsive: true,
                maintainAspectRatio: true,
                tooltips: {
                    titleFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10, 
                    bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                },
                legend: {
                    position: 'top',
                    labels: {
                        fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                    },
                },
            },
            oneHeader: [
                { text: 'ASN', value:'asn', align:'centre', sortable: false}
            ],
            twoHeader: [
                { text: 'ASN', value:'asn', align:'centre', ad_text: 'ASN'},
                { text: 'Breakdown', value:'bdstr', align:'centre', sortable: false, ad_text: 'Breakdown of all countries constituting prefixes owned by this ASN (see info for how this is calculated'},
                { text: 'Proportion this country', value:'perc_specific', align:'centre', ad_text: 'How much of this ASN\'s ranges are geolocated in the searched country. Note that proportion = 0 means less than 0.005%'}
            ],
            refRules: [ v => !!v || 'Please select a country' ],
            distinct_colors: [
                '#e6194b', 
                '#3cb44b', 
                '#ffe119', 
                '#4363d8', 
                '#f58231', 
                '#911eb4', 
                '#46f0f0', 
                '#f032e6', 
                '#bcf60c', 
                '#fabebe', 
                '#008080', 
                '#e6beff', 
                '#9a6324', 
                '#fffac8', 
                '#800000', 
                '#aaffc3', 
                '#808000', 
                '#ffd8b1', 
                '#000075', 
                '#808080', 
                //'#ffffff', remove white
                '#000000'
            ],
            countryCodes: { "Afghanistan": "AF",
                "Ã…land Islands": "AX",
                "Albania": "AL",
                "Algeria": "DZ",
                "American Samoa": "AS",
                "Andorra": "AD",
                "Angola": "AO",
                "Anguilla": "AI",
                "Antarctica": "AQ",
                "Antigua and Barbuda": "AG",
                "Argentina": "AR",
                "Armenia": "AM",
                "Aruba": "AW",
                "Australia": "AU",
                "Austria": "AT",
                "Azerbaijan": "AZ",
                "Bahamas": "BS",
                "Bahrain": "BH",
                "Bangladesh": "BD",
                "Barbados": "BB",
                "Belarus": "BY",
                "Belgium": "BE",
                "Belize": "BZ",
                "Benin": "BJ",
                "Bermuda": "BM",
                "Bhutan": "BT",
                "Bolivia, Plurinational State of": "BO",
                "Bonaire, Sint Eustatius and Saba": "BQ",
                "Bosnia and Herzegovina": "BA",
                "Botswana": "BW",
                "Bouvet Island": "BV",
                "Brazil": "BR",
                "British Indian Ocean Territory": "IO",
                "Brunei Darussalam": "BN",
                "Bulgaria": "BG",
                "Burkina Faso": "BF",
                "Burundi": "BI",
                "Cambodia": "KH",
                "Cameroon": "CM",
                "Canada": "CA",
                "Cape Verde": "CV",
                "Cayman Islands": "KY",
                "Central African Republic": "CF",
                "Chad": "TD",
                "Chile": "CL",
                "China": "CN",
                "Christmas Island": "CX",
                "Cocos (Keeling) Islands": "CC",
                "Colombia": "CO",
                "Comoros": "KM",
                "Congo": "CG",
                "Congo, the Democratic Republic of the": "CD",
                "Cook Islands": "CK",
                "Costa Rica": "CR",
                "CÃ´te d'Ivoire": "CI",
                "Croatia": "HR",
                "Cuba": "CU",
                "CuraÃ§ao": "CW",
                "Cyprus": "CY",
                "Czech Republic": "CZ",
                "Denmark": "DK",
                "Djibouti": "DJ",
                "Dominica": "DM",
                "Dominican Republic": "DO",
                "Ecuador": "EC",
                "Egypt": "EG",
                "El Salvador": "SV",
                "Equatorial Guinea": "GQ",
                "Eritrea": "ER",
                "Estonia": "EE",
                "Ethiopia": "ET",
                "European Union": "EU",
                "Falkland Islands (Malvinas)": "FK",
                "Faroe Islands": "FO",
                "Fiji": "FJ",
                "Finland": "FI",
                "France": "FR",
                "French Guiana": "GF",
                "French Polynesia": "PF",
                "French Southern Territories": "TF",
                "Gabon": "GA",
                "Gambia": "GM",
                "Georgia": "GE",
                "Germany": "DE",
                "Ghana": "GH",
                "Gibraltar": "GI",
                "Greece": "GR",
                "Greenland": "GL",
                "Grenada": "GD",
                "Guadeloupe": "GP",
                "Guam": "GU",
                "Guatemala": "GT",
                "Guernsey": "GG",
                "Guinea": "GN",
                "Guinea-Bissau": "GW",
                "Guyana": "GY",
                "Haiti": "HT",
                "Heard Island and McDonald Islands": "HM",
                "Holy See (Vatican City State)": "VA",
                "Honduras": "HN",
                "Hong Kong": "HK",
                "Hungary": "HU",
                "Iceland": "IS",
                "India": "IN",
                "Indonesia": "ID",
                "Iran, Islamic Republic of": "IR",
                "Iraq": "IQ",
                "Ireland": "IE",
                "Isle of Man": "IM",
                "Israel": "IL",
                "Italy": "IT",
                "Jamaica": "JM",
                "Japan": "JP",
                "Jersey": "JE",
                "Jordan": "JO",
                "Kazakhstan": "KZ",
                "Kenya": "KE",
                "Kiribati": "KI",
                "Korea, Democratic People's Republic of": "KP",
                "Korea, Republic of": "KR",
                "Kuwait": "KW",
                "Kyrgyzstan": "KG",
                "Lao People's Democratic Republic": "LA",
                "Latvia": "LV",
                "Lebanon": "LB",
                "Lesotho": "LS",
                "Liberia": "LR",
                "Libya": "LY",
                "Liechtenstein": "LI",
                "Lithuania": "LT",
                "Luxembourg": "LU",
                "Macao": "MO",
                "Macedonia, the Former Yugoslav Republic of": "MK",
                "Madagascar": "MG",
                "Malawi": "MW",
                "Malaysia": "MY",
                "Maldives": "MV",
                "Mali": "ML",
                "Malta": "MT",
                "Marshall Islands": "MH",
                "Martinique": "MQ",
                "Mauritania": "MR",
                "Mauritius": "MU",
                "Mayotte": "YT",
                "Mexico": "MX",
                "Micronesia, Federated States of": "FM",
                "Moldova, Republic of": "MD",
                "Monaco": "MC",
                "Mongolia": "MN",
                "Montenegro": "ME",
                "Montserrat": "MS",
                "Morocco": "MA",
                "Mozambique": "MZ",
                "Myanmar": "MM",
                "Namibia": "NA",
                "Nauru": "NR",
                "Nepal": "NP",
                "Netherlands": "NL",
                "New Caledonia": "NC",
                "New Zealand": "NZ",
                "Nicaragua": "NI",
                "Niger": "NE",
                "Nigeria": "NG",
                "Niue": "NU",
                "Norfolk Island": "NF",
                "Northern Mariana Islands": "MP",
                "Norway": "NO",
                "Oman": "OM",
                "Pakistan": "PK",
                "Palau": "PW",
                "Palestine, State of": "PS",
                "Panama": "PA",
                "Papua New Guinea": "PG",
                "Paraguay": "PY",
                "Peru": "PE",
                "Philippines": "PH",
                "Pitcairn": "PN",
                "Poland": "PL",
                "Portugal": "PT",
                "Puerto Rico": "PR",
                "Qatar": "QA",
                "RÃ©union": "RE",
                "Romania": "RO",
                "Russian Federation": "RU",
                "Rwanda": "RW",
                "Saint BarthÃ©lemy": "BL",
                "Saint Helena, Ascension and Tristan da Cunha": "SH",
                "Saint Kitts and Nevis": "KN",
                "Saint Lucia": "LC",
                "Saint Martin (French part)": "MF",
                "Saint Pierre and Miquelon": "PM",
                "Saint Vincent and the Grenadines": "VC",
                "Samoa": "WS",
                "San Marino": "SM",
                "Sao Tome and Principe": "ST",
                "Saudi Arabia": "SA",
                "Senegal": "SN",
                "Serbia": "RS",
                "Seychelles": "SC",
                "Sierra Leone": "SL",
                "Singapore": "SG",
                "Sint Maarten (Dutch part)": "SX",
                "Slovakia": "SK",
                "Slovenia": "SI",
                "Solomon Islands": "SB",
                "Somalia": "SO",
                "South Africa": "ZA",
                "South Georgia and the South Sandwich Islands": "GS",
                "South Sudan": "SS",
                "Spain": "ES",
                "Sri Lanka": "LK",
                "Sudan": "SD",
                "Suriname": "SR",
                "Svalbard and Jan Mayen": "SJ",
                "Swaziland": "SZ",
                "Sweden": "SE",
                "Switzerland": "CH",
                "Syrian Arab Republic": "SY",
                "Taiwan, Province of China": "TW",
                "Tajikistan": "TJ",
                "Tanzania, United Republic of": "TZ",
                "Thailand": "TH",
                "Timor-Leste": "TL",
                "Togo": "TG",
                "Tokelau": "TK",
                "Tonga": "TO",
                "Trinidad and Tobago": "TT",
                "Tunisia": "TN",
                "Turkey": "TR",
                "Turkmenistan": "TM",
                "Turks and Caicos Islands": "TC",
                "Tuvalu": "TV",
                "Uganda": "UG",
                "Ukraine": "UA",
                "United Arab Emirates": "AE",
                "United Kingdom": "GB",
                "United States": "US",
                "United States Minor Outlying Islands": "UM",
                "Uruguay": "UY",
                "Uzbekistan": "UZ",
                "Vanuatu": "VU",
                "Venezuela, Bolivarian Republic of": "VE",
                "Viet Nam": "VN",
                "Virgin Islands, British": "VG",
                "Virgin Islands, U.S.": "VI",
                "Wallis and Futuna": "WF",
                "Western Sahara": "EH",
                "Yemen": "YE",
                "Zambia": "ZM",
                "Zimbabwe": "ZW"
            },
            countryItems: [
                "United Kingdom",
                "Afghanistan",
                "Ã…land Islands",
                "Albania",
                "Algeria",
                "American Samoa",
                "Andorra",
                "Angola",
                "Anguilla",
                "Antarctica",
                "Antigua and Barbuda",
                "Argentina",
                "Armenia",
                "Aruba",
                "Australia",
                "Austria",
                "Azerbaijan",
                "Bahamas",
                "Bahrain",
                "Bangladesh",
                "Barbados",
                "Belarus",
                "Belgium",
                "Belize",
                "Benin",
                "Bermuda",
                "Bhutan",
                "Bolivia, Plurinational State of",
                "Bonaire, Sint Eustatius and Saba",
                "Bosnia and Herzegovina",
                "Botswana",
                "Bouvet Island",
                "Brazil",
                "British Indian Ocean Territory",
                "Brunei Darussalam",
                "Bulgaria",
                "Burkina Faso",
                "Burundi",
                "Cambodia",
                "Cameroon",
                "Canada",
                "Cape Verde",
                "Cayman Islands",
                "Central African Republic",
                "Chad",
                "Chile",
                "China",
                "Christmas Island",
                "Cocos (Keeling) Islands",
                "Colombia",
                "Comoros",
                "Congo",
                "Congo, the Democratic Republic of the",
                "Cook Islands",
                "Costa Rica",
                "CÃ´te d'Ivoire",
                "Croatia",
                "Cuba",
                "CuraÃ§ao",
                "Cyprus",
                "Czech Republic",
                "Denmark",
                "Djibouti",
                "Dominica",
                "Dominican Republic",
                "Ecuador",
                "Egypt",
                "El Salvador",
                "Equatorial Guinea",
                "Eritrea",
                "Estonia",
                "European Union",
                "Ethiopia",
                "Falkland Islands (Malvinas)",
                "Faroe Islands",
                "Fiji",
                "Finland",
                "France",
                "French Guiana",
                "French Polynesia",
                "French Southern Territories",
                "Gabon",
                "Gambia",
                "Georgia",
                "Germany",
                "Ghana",
                "Gibraltar",
                "Greece",
                "Greenland",
                "Grenada",
                "Guadeloupe",
                "Guam",
                "Guatemala",
                "Guernsey",
                "Guinea",
                "Guinea-Bissau",
                "Guyana",
                "Haiti",
                "Heard Island and McDonald Islands",
                "Holy See (Vatican City State)",
                "Honduras",
                "Hong Kong",
                "Hungary",
                "Iceland",
                "India",
                "Indonesia",
                "Iran, Islamic Republic of",
                "Iraq",
                "Ireland",
                "Isle of Man",
                "Israel",
                "Italy",
                "Jamaica",
                "Japan",
                "Jersey",
                "Jordan",
                "Kazakhstan",
                "Kenya",
                "Kiribati",
                "Korea, Democratic People's Republic of",
                "Korea, Republic of",
                "Kuwait",
                "Kyrgyzstan",
                "Lao People's Democratic Republic",
                "Latvia",
                "Lebanon",
                "Lesotho",
                "Liberia",
                "Libya",
                "Liechtenstein",
                "Lithuania",
                "Luxembourg",
                "Macao",
                "Macedonia, the Former Yugoslav Republic of",
                "Madagascar",
                "Malawi",
                "Malaysia",
                "Maldives",
                "Mali",
                "Malta",
                "Marshall Islands",
                "Martinique",
                "Mauritania",
                "Mauritius",
                "Mayotte",
                "Mexico",
                "Micronesia, Federated States of",
                "Moldova, Republic of",
                "Monaco",
                "Mongolia",
                "Montenegro",
                "Montserrat",
                "Morocco",
                "Mozambique",
                "Myanmar",
                "Namibia",
                "Nauru",
                "Nepal",
                "Netherlands",
                "New Caledonia",
                "New Zealand",
                "Nicaragua",
                "Niger",
                "Nigeria",
                "Niue",
                "Norfolk Island",
                "Northern Mariana Islands",
                "Norway",
                "Oman",
                "Pakistan",
                "Palau",
                "Palestine, State of",
                "Panama",
                "Papua New Guinea",
                "Paraguay",
                "Peru",
                "Philippines",
                "Pitcairn",
                "Poland",
                "Portugal",
                "Puerto Rico",
                "Qatar",
                "RÃ©union",
                "Romania",
                "Russian Federation",
                "Rwanda",
                "Saint BarthÃ©lemy",
                "Saint Helena, Ascension and Tristan da Cunha",
                "Saint Kitts and Nevis",
                "Saint Lucia",
                "Saint Martin (French part)",
                "Saint Pierre and Miquelon",
                "Saint Vincent and the Grenadines",
                "Samoa",
                "San Marino",
                "Sao Tome and Principe",
                "Saudi Arabia",
                "Senegal",
                "Serbia",
                "Seychelles",
                "Sierra Leone",
                "Singapore",
                "Sint Maarten (Dutch part)",
                "Slovakia",
                "Slovenia",
                "Solomon Islands",
                "Somalia",
                "South Africa",
                "South Georgia and the South Sandwich Islands",
                "South Sudan",
                "Spain",
                "Sri Lanka",
                "Sudan",
                "Suriname",
                "Svalbard and Jan Mayen",
                "Swaziland",
                "Sweden",
                "Switzerland",
                "Syrian Arab Republic",
                "Taiwan, Province of China",
                "Tajikistan",
                "Tanzania, United Republic of",
                "Thailand",
                "Timor-Leste",
                "Togo",
                "Tokelau",
                "Tonga",
                "Trinidad and Tobago",
                "Tunisia",
                "Turkey",
                "Turkmenistan",
                "Turks and Caicos Islands",
                "Tuvalu",
                "Uganda",
                "Ukraine",
                "United Arab Emirates",
                "United States",
                "United States Minor Outlying Islands",
                "Uruguay",
                "Uzbekistan",
                "Vanuatu",
                "Venezuela, Bolivarian Republic of",
                "Viet Nam",
                "Virgin Islands, British",
                "Virgin Islands, U.S.",
                "Wallis and Futuna",
                "Western Sahara",
                "Yemen",
                "Zambia",
                "Zimbabwe"
            ],
            invCountryCodes: {
                "AF": "Afghanistan",
                "AX": "Ã…land Islands",
                "AL": "Albania",
                "DZ": "Algeria",
                "AS": "American Samoa",
                "AD": "Andorra",
                "AO": "Angola",
                "AI": "Anguilla",
                "AQ": "Antarctica",
                "AG": "Antigua and Barbuda",
                "AR": "Argentina",
                "AM": "Armenia",
                "AW": "Aruba",
                "AU": "Australia",
                "AT": "Austria",
                "AZ": "Azerbaijan",
                "BS": "Bahamas",
                "BH": "Bahrain",
                "BD": "Bangladesh",
                "BB": "Barbados",
                "BY": "Belarus",
                "BE": "Belgium",
                "BZ": "Belize",
                "BJ": "Benin",
                "BM": "Bermuda",
                "BT": "Bhutan",
                "BO": "Bolivia, Plurinational State of",
                "BQ": "Bonaire, Sint Eustatius and Saba",
                "BA": "Bosnia and Herzegovina",
                "BW": "Botswana",
                "BV": "Bouvet Island",
                "BR": "Brazil",
                "IO": "British Indian Ocean Territory",
                "BN": "Brunei Darussalam",
                "BG": "Bulgaria",
                "BF": "Burkina Faso",
                "BI": "Burundi",
                "KH": "Cambodia",
                "CM": "Cameroon",
                "CA": "Canada",
                "CV": "Cape Verde",
                "KY": "Cayman Islands",
                "CF": "Central African Republic",
                "TD": "Chad",
                "CL": "Chile",
                "CN": "China",
                "CX": "Christmas Island",
                "CC": "Cocos (Keeling) Islands",
                "CO": "Colombia",
                "KM": "Comoros",
                "CG": "Congo",
                "CD": "Congo, the Democratic Republic of the",
                "CK": "Cook Islands",
                "CR": "Costa Rica",
                "CI": "CÃ´te d'Ivoire",
                "HR": "Croatia",
                "CU": "Cuba",
                "CW": "CuraÃ§ao",
                "CY": "Cyprus",
                "CZ": "Czech Republic",
                "DK": "Denmark",
                "DJ": "Djibouti",
                "DM": "Dominica",
                "DO": "Dominican Republic",
                "EC": "Ecuador",
                "EG": "Egypt",
                "EU": "European Union",
                "SV": "El Salvador",
                "GQ": "Equatorial Guinea",
                "ER": "Eritrea",
                "EE": "Estonia",
                "ET": "Ethiopia",
                "FK": "Falkland Islands (Malvinas)",
                "FO": "Faroe Islands",
                "FJ": "Fiji",
                "FI": "Finland",
                "FR": "France",
                "GF": "French Guiana",
                "PF": "French Polynesia",
                "TF": "French Southern Territories",
                "GA": "Gabon",
                "GM": "Gambia",
                "GE": "Georgia",
                "DE": "Germany",
                "GH": "Ghana",
                "GI": "Gibraltar",
                "GR": "Greece",
                "GL": "Greenland",
                "GD": "Grenada",
                "GP": "Guadeloupe",
                "GU": "Guam",
                "GT": "Guatemala",
                "GG": "Guernsey",
                "GN": "Guinea",
                "GW": "Guinea-Bissau",
                "GY": "Guyana",
                "HT": "Haiti",
                "HM": "Heard Island and McDonald Islands",
                "VA": "Holy See (Vatican City State)",
                "HN": "Honduras",
                "HK": "Hong Kong",
                "HU": "Hungary",
                "IS": "Iceland",
                "IN": "India",
                "ID": "Indonesia",
                "IR": "Iran, Islamic Republic of",
                "IQ": "Iraq",
                "IE": "Ireland",
                "IM": "Isle of Man",
                "IL": "Israel",
                "IT": "Italy",
                "JM": "Jamaica",
                "JP": "Japan",
                "JE": "Jersey",
                "JO": "Jordan",
                "KZ": "Kazakhstan",
                "KE": "Kenya",
                "KI": "Kiribati",
                "KP": "Korea, Democratic People's Republic of",
                "KR": "Korea, Republic of",
                "KW": "Kuwait",
                "KG": "Kyrgyzstan",
                "LA": "Lao People's Democratic Republic",
                "LV": "Latvia",
                "LB": "Lebanon",
                "LS": "Lesotho",
                "LR": "Liberia",
                "LY": "Libya",
                "LI": "Liechtenstein",
                "LT": "Lithuania",
                "LU": "Luxembourg",
                "MO": "Macao",
                "MK": "Macedonia, the Former Yugoslav Republic of",
                "MG": "Madagascar",
                "MW": "Malawi",
                "MY": "Malaysia",
                "MV": "Maldives",
                "ML": "Mali",
                "MT": "Malta",
                "MH": "Marshall Islands",
                "MQ": "Martinique",
                "MR": "Mauritania",
                "MU": "Mauritius",
                "YT": "Mayotte",
                "MX": "Mexico",
                "FM": "Micronesia, Federated States of",
                "MD": "Moldova, Republic of",
                "MC": "Monaco",
                "MN": "Mongolia",
                "ME": "Montenegro",
                "MS": "Montserrat",
                "MA": "Morocco",
                "MZ": "Mozambique",
                "MM": "Myanmar",
                "NA": "Namibia",
                "NR": "Nauru",
                "NP": "Nepal",
                "NL": "Netherlands",
                "NC": "New Caledonia",
                "NZ": "New Zealand",
                "NI": "Nicaragua",
                "NE": "Niger",
                "NG": "Nigeria",
                "NU": "Niue",
                "NF": "Norfolk Island",
                "MP": "Northern Mariana Islands",
                "NO": "Norway",
                "OM": "Oman",
                "PK": "Pakistan",
                "PW": "Palau",
                "PS": "Palestine, State of",
                "PA": "Panama",
                "PG": "Papua New Guinea",
                "PY": "Paraguay",
                "PE": "Peru",
                "PH": "Philippines",
                "PN": "Pitcairn",
                "PL": "Poland",
                "PT": "Portugal",
                "PR": "Puerto Rico",
                "QA": "Qatar",
                "RE": "RÃ©union",
                "RO": "Romania",
                "RU": "Russian Federation",
                "RW": "Rwanda",
                "BL": "Saint BarthÃ©lemy",
                "SH": "Saint Helena, Ascension and Tristan da Cunha",
                "KN": "Saint Kitts and Nevis",
                "LC": "Saint Lucia",
                "MF": "Saint Martin (French part)",
                "PM": "Saint Pierre and Miquelon",
                "VC": "Saint Vincent and the Grenadines",
                "WS": "Samoa",
                "SM": "San Marino",
                "ST": "Sao Tome and Principe",
                "SA": "Saudi Arabia",
                "SN": "Senegal",
                "RS": "Serbia",
                "SC": "Seychelles",
                "SL": "Sierra Leone",
                "SG": "Singapore",
                "SX": "Sint Maarten (Dutch part)",
                "SK": "Slovakia",
                "SI": "Slovenia",
                "SB": "Solomon Islands",
                "SO": "Somalia",
                "ZA": "South Africa",
                "GS": "South Georgia and the South Sandwich Islands",
                "SS": "South Sudan",
                "ES": "Spain",
                "LK": "Sri Lanka",
                "SD": "Sudan",
                "SR": "Suriname",
                "SJ": "Svalbard and Jan Mayen",
                "SZ": "Swaziland",
                "SE": "Sweden",
                "CH": "Switzerland",
                "SY": "Syrian Arab Republic",
                "TW": "Taiwan, Province of China",
                "TJ": "Tajikistan",
                "TZ": "Tanzania, United Republic of",
                "TH": "Thailand",
                "TL": "Timor-Leste",
                "TG": "Togo",
                "TK": "Tokelau",
                "TO": "Tonga",
                "TT": "Trinidad and Tobago",
                "TN": "Tunisia",
                "TR": "Turkey",
                "TM": "Turkmenistan",
                "TC": "Turks and Caicos Islands",
                "TV": "Tuvalu",
                "UG": "Uganda",
                "UA": "Ukraine",
                "AE": "United Arab Emirates",
                "GB": "United Kingdom",
                "US": "United States",
                "UM": "United States Minor Outlying Islands",
                "UY": "Uruguay",
                "UZ": "Uzbekistan",
                "VU": "Vanuatu",
                "VE": "Venezuela, Bolivarian Republic of",
                "VN": "Viet Nam",
                "VG": "Virgin Islands, British",
                "VI": "Virgin Islands, U.S.",
                "WF": "Wallis and Futuna",
                "EH": "Western Sahara",
                "YE": "Yemen",
                "ZM": "Zambia",
                "ZW": "Zimbabwe"
            }
        }
    },
    

}
</script>

<style scoped>

    .v-autocomplete .v-autocomplete-input-group .v-autocomplete-input {
       font-size: var(--textsize);
    }

    tbody tr:nth-of-type(even) {
      background-color: rgba(0, 0, 0, .05);
    }

    .v-table tr:hover:not(.v-table__expanded__content) {
        background-color: rgba(0, 0, 0, .4);
    }

</style>